import React, { useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const field = "w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:border-[#002FA7]";
const PRICE = 0.077;
const MIN = 500;

// Admin: generate a ready-to-send quote (devis) PDF for a school.
export function AdminQuote() {
  const [f, setF] = useState({ org_name: "", org_address: "", contact_name: "", contact_email: "", teachers: 20, credits_per_teacher: 50 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const credits = Math.max(MIN, Number(f.teachers || 0) * Number(f.credits_per_teacher || 0));
  const total = (credits * PRICE).toFixed(2).replace(".", ",");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/quotes", {
        ...f, contact_email: f.contact_email || null,
        teachers: Number(f.teachers), credits_per_teacher: Number(f.credits_per_teacher),
      }, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "SUX1-devis.pdf";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Devis ${name} téléchargé.`);
    } catch {
      toast("Création du devis impossible. Vérifiez les champs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-border p-6 max-w-3xl mt-6" data-testid="admin-quote">
      <div className="sux-marker mb-2 flex items-center gap-2"><FileText className="w-3 h-3" /> Devis<InfoTip testid="admin-quote-info" content={"Devis PDF sur mesure : indiquez le nombre d'enseignants et de crédits par enseignant, SUX1 calcule le total. Pour les packs standard, l'école peut faire son devis elle-même."} /></div>
      <h3 className="text-xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>Devis établissement</h3>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input required minLength={2} value={f.org_name} onChange={set("org_name")} placeholder="Établissement (ex. Collège Jean Moulin)" className={field} data-testid="admin-quote-org-input" />
        <input value={f.contact_name} onChange={set("contact_name")} placeholder="Contact (ex. Mme Dupont, principale)" className={field} data-testid="admin-quote-contact-input" />
        <textarea rows={2} value={f.org_address} onChange={set("org_address")} placeholder="Adresse (2 lignes)" className={field} data-testid="admin-quote-address-input" />
        <input type="email" value={f.contact_email} onChange={set("contact_email")} placeholder="Email du contact" className={field} data-testid="admin-quote-email-input" />
        <label className="text-xs text-muted-foreground">Enseignants
          <input type="number" min={1} required value={f.teachers} onChange={set("teachers")} className={field} data-testid="admin-quote-teachers-input" />
        </label>
        <label className="text-xs text-muted-foreground">Crédits par enseignant
          <input type="number" min={1} required value={f.credits_per_teacher} onChange={set("credits_per_teacher")} className={field} data-testid="admin-quote-per-teacher-input" />
        </label>
        <p className="md:col-span-2 text-sm" data-testid="admin-quote-total">
          {credits} crédits × 0,077 € = <b className="text-[#002FA7]">{total} €</b>
          {credits === MIN && Number(f.teachers) * Number(f.credits_per_teacher) < MIN && " (minimum de 500 crédits appliqué)"}
        </p>
        <button type="submit" disabled={busy} data-testid="admin-quote-submit"
          className="md:col-span-2 font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 flex justify-center">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Télécharger le devis PDF"}
        </button>
      </form>
    </section>
  );
}

export default AdminQuote;
